/**
 * Maps backend `error_code` envelope values to Story-Output error
 * variants. Single source of truth for the routing logic in
 * `OutputRoute.tsx` + `StoryRoute.tsx` (Batch 12 Sub-phase B).
 *
 * Briefing Kap. 8 (line 1181-1213) lists 4 Story-Output error states;
 * Sub-phase B ships 3 of them (technical / limitBlock / storyNotFound).
 * Timeout deferred to post-launch — see consolidated tracker PWA Offen
 * #56.
 *
 * Default fallback is `'technical'` so any unknown / missing
 * error_code (network failure, malformed envelope, undefined) renders
 * the safest user-facing variant: "Oops! Da ist etwas schiefgelaufen"
 * + Retry CTA.
 */

export type ErrorVariant = 'technical' | 'limitBlock' | 'storyNotFound';

export const ERROR_CODE_TO_VARIANT: Record<string, ErrorVariant> = {
  // Limit-Block — backend short-circuit at credits check (Phase 2E.1)
  // OR webhook-triggered mid-flow drain (Option B, defensive surface).
  blocked_limit: 'limitBlock',

  // Story-Not-Found — direct nav to deleted/non-existent story id.
  // Backend `StoriesService.findOne()` throws `story_not_found`.
  story_not_found: 'storyNotFound',

  // Technical-Failure — explicit known codes that route to the
  // generic Flitzi error. The default fallback below catches the rest.
  llm_unavailable: 'technical',
  system_error: 'technical',
  validation_error: 'technical',
};

/**
 * Resolve an `error_code` string (or undefined / unknown value) to a
 * Story-Output error variant. Defaults to `'technical'` so the user
 * always sees an actionable error UI.
 */
export function getErrorVariant(errorCode?: string | null): ErrorVariant {
  if (!errorCode) return 'technical';
  return ERROR_CODE_TO_VARIANT[errorCode] ?? 'technical';
}
